import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import {
  isAnonymousScope,
  pruneOtherUserScopes,
  userScopeId,
  userScopedKey,
} from '@/utils/userScopedSessionKey';

/**
 * The sessionStorage key `baseKey` should be read/written under, namespaced to
 * the signed-in identity (username + current role).
 *
 * Callers may read the returned key during their first render — every consumer
 * sits behind `AuthGuard`, which only renders children once `checkAuthStatus`
 * has resolved, so the identity is already in the store by then. Should it not
 * be (storage read outside a guarded route), the scope falls back to
 * `anonymous`, which fails closed: nothing another user saved is restored.
 *
 * Entries left in the tab by other identities — including the legacy unscoped
 * `baseKey` — are pruned on mount.
 */
export function useUserScopedSessionKey(baseKey: string): string {
  const user = useSelector((state: WARootState) => state.auth.user);
  const role = useSelector((state: WARootState) => state.auth.currentRole);

  const scopeId = useMemo(() => userScopeId(user, role), [user, role]);

  useEffect(() => {
    // Never prune while unidentified: an anonymous render must not delete the
    // snapshot the signed-in user is about to restore.
    if (isAnonymousScope(scopeId)) return;
    pruneOtherUserScopes(baseKey, scopeId);
  }, [baseKey, scopeId]);

  return useMemo(() => userScopedKey(baseKey, scopeId), [baseKey, scopeId]);
}
